/**
 * Event Update Handler Registry
 * ──────────────────────────────
 * Central registry mapping SSE event types to state-update handlers.
 *
 * HOW IT WORKS:
 * When an SSE ChangeEvent arrives, the EventDispatcher (useEventDispatcher hook)
 * looks up handlers from this registry and executes them with a StateUpdaters
 * object that provides fine-grained mutation methods for app state.
 *
 * HOW TO ADD A NEW HANDLER:
 * 1. Add an entry to EVENT_HANDLERS below, keyed by the event type string
 *    (use ChangeEventTypes constants).
 * 2. Implement the `handle` function using the StateUpdaters methods.
 * 3. Optionally set config: skipSelfEvents, debounceMs, debounceKey.
 * 4. That's it — no other files need to change.
 *
 * MULTIPLE HANDLERS PER EVENT:
 * Each event type maps to an array of handlers. Different parts of the app can
 * independently respond to the same event (e.g., photo.added updates album
 * metadata AND invalidates the photo cache — these are separate handlers).
 */

import { ChangeEvent, ChangeEventTypes } from './notificationService';
import { Album } from './photoService';

// ── Types ─────────────────────────────────────────────────────────────────────

/** Fine-grained state mutation methods collected from React contexts. */
export interface StateUpdaters {
  albums: {
    /** Insert or replace a single album in the cache (matched by ID). */
    upsert: (album: Album) => void;
    /** Add a new album at the top of the list (skips if ID already exists). */
    prepend: (album: Album) => void;
    /** Remove an album from the cache by ID (no-op if not found). */
    remove: (albumId: string) => void;
    /** Merge partial fields into an existing album entry. */
    patch: (albumId: string, fields: Partial<Album>) => void;
  };
  photos: {
    /** Clear the photo cache for one album (forces re-fetch on next visit). */
    invalidate: (albumId: string) => void;
    /** Clear photo caches for all albums. */
    invalidateAll: () => void;
  };
  friends: {
    /** Signal that the friends list may have changed. */
    refresh: () => void;
  };
  /** Helpers for fetching data within handlers. */
  fetch: {
    /** Fetch a single album by ID from the API. */
    albumById: (albumId: string) => Promise<Album>;
  };
}

/** Per-handler configuration options. */
export interface EventHandlerConfig {
  /** If true, the dispatcher skips this handler when event.actorUserId === currentUserId. */
  skipSelfEvents?: boolean;
  /** Custom debounce key function. Events producing the same key are debounced.
   *  Default: `${event.type}:${event.albumId ?? ''}` */
  debounceKey?: (event: ChangeEvent) => string;
  /** Debounce delay in ms. When set, rapid events with the same debounce key
   *  are collapsed — only the last one executes after the delay. Default: 0 (no debounce). */
  debounceMs?: number;
}

/** A single event handler. */
export interface EventHandler {
  /** The function that applies state updates in response to an event. */
  handle: (event: ChangeEvent, updaters: StateUpdaters) => void | Promise<void>;
  /** Optional configuration (debouncing, self-event filtering). */
  config?: EventHandlerConfig;
}

// ── Handler Registry ──────────────────────────────────────────────────────────

export const EVENT_HANDLERS: Record<string, EventHandler[]> = {
  // ─── Album events ───────────────────────────────────────────────────────

  [ChangeEventTypes.AlbumCreated]: [{
    handle: async (event, { albums, fetch }) => {
      if (!event.albumId) return;
      const album = await fetch.albumById(event.albumId);
      // Only show albums that have photos (with a thumbnail);
      // empty albums will be surfaced later by the PhotoAdded handler.
      if (album.photoCount > 0) {
        albums.prepend(album);
      }
    },
    // User already sees the album from the creation flow; skip to avoid duplicates.
    config: { skipSelfEvents: true },
  }],

  [ChangeEventTypes.AlbumUpdated]: [{
    handle: async (event, { albums, fetch }) => {
      if (!event.albumId) return;
      const album = await fetch.albumById(event.albumId);
      albums.upsert(album);
    },
  }],

  [ChangeEventTypes.AlbumDeleted]: [{
    handle: (event, { albums, photos }) => {
      if (!event.albumId) return;
      albums.remove(event.albumId);
      photos.invalidate(event.albumId);
    },
  }],

  // ─── Photo events ───────────────────────────────────────────────────────

  [ChangeEventTypes.PhotoAdded]: [{
    handle: async (event, { albums, photos, fetch }) => {
      if (!event.albumId) return;
      // Refresh album metadata (photoCount, thumbnail, dates).
      // Upsert updates existing albums in place; for new albums (e.g. friend's
      // album getting first photos), the useAlbumList hook prepends them.
      const album = await fetch.albumById(event.albumId);
      albums.upsert(album);
      // Invalidate photo cache so gallery re-fetches on next visit
      photos.invalidate(event.albumId);
    },
    // Bulk uploads produce many rapid events — debounce into one fetch.
    config: { debounceMs: 500 },
  }],

  [ChangeEventTypes.PhotoDeleted]: [{
    handle: async (event, { albums, photos, fetch }) => {
      if (!event.albumId) return;
      const album = await fetch.albumById(event.albumId);
      albums.upsert(album);
      photos.invalidate(event.albumId);
    },
    config: { debounceMs: 500 },
  }],

  [ChangeEventTypes.PhotoReordered]: [{
    handle: (event, { photos }) => {
      if (!event.albumId) return;
      photos.invalidate(event.albumId);
    },
  }],

  [ChangeEventTypes.PhotoUpdated]: [{
    handle: (event, { photos }) => {
      if (!event.albumId) return;
      photos.invalidate(event.albumId);
    },
  }],

  // ─── Friendship events ──────────────────────────────────────────────────

  [ChangeEventTypes.FriendshipCreated]: [{
    handle: (_event, { friends }) => {
      friends.refresh();
    },
  }],

  [ChangeEventTypes.FriendshipRemoved]: [{
    handle: (_event, { friends }) => {
      friends.refresh();
    },
  }],

  // ─── Comment & Reaction events ──────────────────────────────────────────
  // These don't impact album/photo caches. The ActivityFeed has its own
  // subscription for these event types (incremental prepend).
  // To add cache-level handling in the future, add entries here:
  //
  // [ChangeEventTypes.CommentAdded]: [{ handle: ... }],
  // [ChangeEventTypes.ReactionAdded]: [{ handle: ... }],
};
